import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatBRL, ESTAGIOS, initials, whatsappHref, apiErrorMsg } from "@/lib/api";
import { ResponsavelNfFields, respNfInvalido } from "@/components/ResponsavelNfFields";
import { InscricaoGeralAviso } from "@/components/InscricaoGeralAviso";
import { CortesiaFields, cortesiaErro } from "@/components/CortesiaFields";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Phone, MessageCircle, Plus, X, Target, DollarSign, TrendingUp, ChevronRight, ChevronLeft, Star, Send, Ban, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";
import ContatoRegistrarModal from "@/components/ContatoRegistrarModal";
import ConfirmacaoEmailModal from "@/components/ConfirmacaoEmailModal";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors,
  useDroppable, useDraggable, closestCenter,
} from "@dnd-kit/core";

/* ---------- Card ---------- */
function KanbanCard({ card, mun, vend, onOpen, onExp, isVendedora, canDrag = true, medalMun, medalIsTop, onCancelar, inscricaoQtd, inscricaoCortesias = 0, inscricaoValor, expReceita, propostaEnviada, confirmacaoEnviada, onOpenEditInsc }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, disabled: !canDrag });
  const style = isDragging ? { opacity: 0.4 } : undefined;
  const showExp = ["incerto", "interessado", "certo"].includes(card.estagio);
  const isCancelada = !!card.cancelada_em;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(canDrag && !isCancelada ? listeners : {})}
      {...(canDrag && !isCancelada ? attributes : {})}
      className={`relative bg-white rounded-lg border shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow p-3 touch-none overflow-hidden ${
        isCancelada
          ? "border-red-400 border-2 bg-red-50/40 cursor-default"
          : `border-slate-200 ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`
      } ${(!canDrag && isVendedora && !isCancelada) ? "opacity-60 bg-slate-50 border-dashed" : ""}`}
      data-testid={`card-${card.id}`}
    >
      {isCancelada && (
        <div className="absolute top-1.5 right-1.5 z-10 bg-red-600 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shadow-sm">
          Cancelada
        </div>
      )}
      {/* Tarja da vendedora */}
      <div className={`-mx-3 -mt-3 mb-2 px-3 py-1 text-[10px] font-bold uppercase tracking-widest flex items-center justify-between ${
        isCancelada ? "bg-red-600 text-white" : canDrag ? "bg-blue-600 text-white" : "bg-slate-500 text-white/95"
      }`}>
        <div className="flex items-center gap-1.5 truncate">
          <Avatar className="w-4 h-4 flex-shrink-0">
            <AvatarFallback className="text-[8px] bg-white/25 text-white font-bold">
              {initials(vend?.nome)}
            </AvatarFallback>
          </Avatar>
          <span className="truncate">
            {vend?.codigo && <span className="opacity-80 mr-1">{vend.codigo}</span>}
            {vend?.nome || "—"}
          </span>
        </div>
        {!canDrag && isVendedora && (
          <span className="text-white/70 text-[9px]" title="Você não pode mover este card (é de outra vendedora)">🔒</span>
        )}
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Iter 42 (item #5): se está em Inscrito e temos handler dedicado, abre editor
              if (card.estagio === "inscrito" && !card.cancelada_em && typeof onOpenEditInsc === "function") {
                onOpenEditInsc(card);
              } else {
                onOpen(card);
              }
            }}
            className="text-left font-semibold text-slate-900 hover:text-blue-600 truncate w-full flex items-center gap-1"
            data-testid={`card-open-${card.id}`}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <span className="truncate">{mun?.nome || "..."}</span>
            {medalMun >= 5 && <span title={`${medalMun} inscrições no ano`} className="text-amber-500 text-sm">🥇</span>}
            {medalIsTop && <span title="Top 10 do ano" className="text-yellow-500 text-sm">🏆</span>}
          </button>
          <div className="text-xs text-slate-600 mt-0.5 font-medium flex items-center gap-1 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">
              {mun?.uf}
            </span>
            <span className="inline-flex items-center gap-1 bg-blue-50 rounded px-1.5 py-0.5 text-blue-700">
              {mun?.tipo_orgao || "Câmara"}
            </span>
            {(card.pessoas_ids?.length || 0) > 0 && card.estagio !== "inscrito" && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 rounded px-1.5 py-0.5 text-emerald-700 font-bold">
                {card.pessoas_ids.length} {card.pessoas_ids.length === 1 ? "pessoa" : "pessoas"}
              </span>
            )}
            {card.estagio === "inscrito" && inscricaoQtd > 0 && !card.cancelada_em && (
              <span className="inline-flex items-center gap-1 bg-emerald-100 rounded px-1.5 py-0.5 text-emerald-800 font-bold">
                ✓ {inscricaoQtd} inscrito{inscricaoQtd === 1 ? "" : "s"}
                {inscricaoCortesias > 0 && (
                  <span className="ml-1 text-sky-800" data-testid={`card-cortesias-${card.id}`}>· {inscricaoCortesias} cortesia{inscricaoCortesias === 1 ? "" : "s"}</span>
                )}
                {inscricaoValor > 0 && (
                  <span className="ml-1 text-emerald-900" data-testid={`card-receita-${card.id}`}>
                    · {formatBRL(inscricaoValor)}
                  </span>
                )}
              </span>
            )}
            {card.estagio === "certo" && expReceita > 0 && (
              <span className="inline-flex items-center gap-1 bg-sky-50 rounded px-1.5 py-0.5 text-sky-700 font-bold" title="Receita esperada (expectativa × valor tabela)" data-testid={`card-expreceita-${card.id}`}>
                💰 {formatBRL(expReceita)}
              </span>
            )}
            {propostaEnviada && (
              <span className="inline-flex items-center gap-1 bg-purple-50 rounded px-1.5 py-0.5 text-purple-700 font-bold" title="Proposta gerada">
                📄 Proposta
              </span>
            )}
            {confirmacaoEnviada && (
              <span className="inline-flex items-center gap-1 bg-blue-50 rounded px-1.5 py-0.5 text-blue-700 font-bold" title="Confirmação enviada por e-mail">
                ✉️ Confirmação
              </span>
            )}
          </div>
        </div>
      </div>

      {showExp && !(isVendedora && !canDrag) && (
        <div
          className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="text-xs text-slate-500">Expect.:</div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onExp(card, "min", -1)}
              className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold"
              data-testid={`exp-min-dec-${card.id}`}
            >−</button>
            <span className="text-sm tabular-nums font-semibold w-6 text-center">{card.exp_min}</span>
            <button
              onClick={() => onExp(card, "min", 1)}
              className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold"
              data-testid={`exp-min-inc-${card.id}`}
            >+</button>
          </div>
          <div className="text-xs text-slate-400">a</div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onExp(card, "max", -1)}
              className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold"
              data-testid={`exp-max-dec-${card.id}`}
            >−</button>
            <span className="text-sm tabular-nums font-semibold w-6 text-center">{card.exp_max}</span>
            <button
              onClick={() => onExp(card, "max", 1)}
              className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold"
              data-testid={`exp-max-inc-${card.id}`}
            >+</button>
          </div>
        </div>
      )}

      {/* Iter 36: Card no estágio Inscrito */}
      {card.estagio === "inscrito" && (
        <div
          className="mt-3 pt-3 border-t border-slate-100"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isCancelada ? (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Inscrição cancelada</div>
              {card.valor_cancelado != null && (
                <div className="text-sm text-slate-400 line-through tabular-nums">
                  {formatBRL(card.valor_cancelado)} · {card.qtd_cancelada || 0} inscrito{card.qtd_cancelada === 1 ? "" : "s"}
                </div>
              )}
              {card.cancelamento_motivo && (
                <div className="text-[11px] text-slate-500 italic truncate" title={card.cancelamento_motivo}>
                  Motivo: {card.cancelamento_motivo}
                </div>
              )}
            </div>
          ) : (
            onCancelar && !(isVendedora && !canDrag) && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCancelar(card); }}
                className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-red-600 hover:text-white bg-white hover:bg-red-600 border border-red-300 rounded-md px-2 py-1.5 transition-colors"
                data-testid={`btn-cancelar-card-${card.id}`}
              >
                <Ban className="w-3 h-3" /> Cancelar inscrição
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Column ---------- */
function KanbanColumn({ estagio, cards, muns, users, onOpen, onExp, isVendedora, currentUserId, medals, colMetrics, onCancelar, inscricoesMap, cortesiasMap, receitaMap, propostasMap, confirmacoesMap, onOpenEditInsc }) {
  const { setNodeRef, isOver } = useDroppable({ id: estagio.key });
  const m = colMetrics || {};
  return (
    <div
      ref={setNodeRef}
      className={`col-${estagio.key} rounded-xl flex flex-col min-h-[400px] ${isOver ? "ring-2 ring-blue-400" : ""}`}
      data-testid={`col-${estagio.key}`}
    >
      <div className="px-3 pt-3 pb-2 border-b border-black/5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className={`chip-${estagio.key} text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide`}>
            {estagio.label}
          </span>
          <span className="text-[10px] md:text-xs text-slate-500 font-semibold">{cards.length}</span>
        </div>
        <div className="text-[10px] md:text-[11px] text-slate-600 tabular-nums font-medium flex items-center justify-between gap-1" data-testid={`col-metrics-${estagio.key}`}>
          <span className="truncate">{m.leftLabel}: <b className="text-slate-900">{m.left}</b></span>
          <span className="text-slate-300">·</span>
          <span className="truncate">{m.rightLabel}: <b className="text-slate-900">{m.right}</b></span>
        </div>
      </div>
      <div className="px-2 py-3 space-y-2 overflow-y-auto flex-1 min-h-[100px]">
        {cards.map((c) => {
          const rc = receitaMap?.[c.id] || {};
          return (
            <KanbanCard
              key={c.id}
              card={c}
              mun={muns.find((mn) => mn.id === c.municipio_id)}
              vend={users.find((u) => u.id === c.vendedora_id)}
              onOpen={onOpen}
              onExp={onExp}
              isVendedora={isVendedora}
              canDrag={!isVendedora || c.vendedora_id === currentUserId}
              medalMun={medals?.municipios?.[c.municipio_id] || 0}
              medalIsTop={medals?.top_municipios_ids?.includes(c.municipio_id)}
              onCancelar={onCancelar}
              inscricaoQtd={inscricoesMap?.[c.id] || 0}
              inscricaoCortesias={cortesiasMap?.[c.id] || 0}
              inscricaoValor={rc.valor_vendido || 0}
              expReceita={rc.exp_max_receita || 0}
              propostaEnviada={propostasMap?.[c.id] || false}
              confirmacaoEnviada={confirmacoesMap?.[c.id] || false}
              onOpenEditInsc={onOpenEditInsc}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Side Panel ---------- */
function SidePanel({ open, onClose, card, mun, curso, onInscricao, onContato, reload }) {
  const [pessoas, setPessoas] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [stats12m, setStats12m] = useState(null);
  const [novaPessoaOpen, setNovaPessoaOpen] = useState(false);
  const [newP, setNewP] = useState({ nome: "", funcao: "Vereador", telefone: "", partido: "", whatsapp: "", email: "", instagram: "", data_nascimento: "" });
  const [contatoModal, setContatoModal] = useState(null); // { pessoa, canal }
  const [enviarModal, setEnviarModal] = useState(null); // { pessoa }

  const loadPanel = async () => {
    if (!mun) return;
    const [p, c, s] = await Promise.all([
      api.get(`/pessoas?municipio_id=${mun.id}`),
      api.get(`/contatos?municipio_id=${mun.id}`),
      api.get(`/municipios/${mun.id}/stats-12m`).catch(() => ({ data: null })),
    ]);
    // Filtra pessoas SÓ do card atual (fase do funil) — item 5 da nova diretriz.
    // Se o card não tem pessoas_ids, mostra todas (fallback compatível).
    const pessoasIds = card?.pessoas_ids || [];
    const filtered = pessoasIds.length > 0
      ? p.data.filter((x) => pessoasIds.includes(x.id))
      : p.data;
    setPessoas(filtered);
    setContatos(c.data);
    setStats12m(s.data);
  };

  useEffect(() => {
    if (!open || !mun) return;
    loadPanel();
    // eslint-disable-next-line
  }, [open, mun?.id]);

  const onContatoSaved = () => {
    setContatoModal(null);
    loadPanel();
    onContato?.();
  };

  const setEstrelas = async (pessoa, estrelas) => {
    try {
      await api.patch(`/pessoas/${pessoa.id}/estrelas`, { estrelas });
      setPessoas((prev) => prev.map((p) => p.id === pessoa.id ? { ...p, estrelas } : p));
    } catch { toast.error("Erro"); }
  };

  const togglePontoFocal = async (pessoa) => {
    const novo = !pessoa.ponto_focal;
    try {
      await api.patch(`/pessoas/${pessoa.id}/ponto-focal`, { ponto_focal: novo });
      setPessoas((prev) => prev.map((p) => {
        if (p.id === pessoa.id) return { ...p, ponto_focal: novo };
        if (novo && p.ponto_focal) return { ...p, ponto_focal: false };
        return p;
      }));
      toast.success(novo ? `${pessoa.nome} é o Ponto Focal` : "Ponto Focal removido");
    } catch { toast.error("Erro"); }
  };

  const salvarNovaPessoa = async () => {
    if (!newP.nome.trim()) return toast.error("Informe o nome");
    try {
      const { data } = await api.post("/pessoas", {
        nome: newP.nome, funcao: newP.funcao, telefone: newP.telefone, municipio_id: mun.id,
        partido: newP.partido, whatsapp: newP.whatsapp, email: newP.email || null,
        instagram: newP.instagram, data_nascimento: newP.data_nascimento || null,
      });
      setPessoas((prev) => [...prev, data]);
      setNewP({ nome: "", funcao: "Vereador", telefone: "", partido: "", whatsapp: "", email: "", instagram: "", data_nascimento: "" });
      setNovaPessoaOpen(false);
      toast.success("Pessoa cadastrada");
    } catch { toast.error("Erro ao criar pessoa"); }
  };

  if (!card || !mun) return null;

  // Ordena: ponto focal primeiro, depois estrelas desc, depois nome
  const pessoasOrdenadas = [...pessoas].sort((a, b) => {
    if (a.ponto_focal && !b.ponto_focal) return -1;
    if (!a.ponto_focal && b.ponto_focal) return 1;
    if ((b.estrelas || 0) !== (a.estrelas || 0)) return (b.estrelas || 0) - (a.estrelas || 0);
    return (a.nome || "").localeCompare(b.nome || "");
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="side-panel">
        <SheetHeader className="p-6 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="font-display text-2xl">{mun.nome}</SheetTitle>
          <div className="text-sm text-slate-500">{mun.uf} · {mun.tipo_orgao}</div>

          {/* Stats 12 meses */}
          <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 p-3">
            <div>
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-widest">12m · Inscrições</div>
              <div className="font-display text-lg font-bold tabular-nums text-emerald-700">{stats12m?.qtd_inscricoes ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-blue-700 tracking-widest">12m · Alunos</div>
              <div className="font-display text-lg font-bold tabular-nums text-blue-700">{stats12m?.total_inscritos ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-600 tracking-widest">12m · Valor</div>
              <div className="font-display text-lg font-bold tabular-nums text-slate-800">{formatBRL(stats12m?.valor_total || 0)}</div>
            </div>
          </div>

          <div className="flex gap-2 mt-3">
            <Button
              onClick={() => onInscricao(card)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11 font-semibold"
              data-testid="btn-inscricao"
            >
              🎯 Registrar Inscrição
            </Button>
            <Button
              onClick={() => window.open(`/proposta/${card.id}`, "_blank")}
              variant="outline"
              className="h-11 border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="btn-proposta"
            >
              📄 Proposta
            </Button>
            <Button
              onClick={() => setNovaPessoaOpen(true)}
              variant="outline"
              className="h-11"
              data-testid="btn-nova-pessoa"
            >
              <Plus className="w-4 h-4 mr-1" /> Pessoa
            </Button>
          </div>
        </SheetHeader>

        <div className="p-6 space-y-6">
          {/* Pessoas */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Pessoas ({pessoas.length})
              </div>
              <div className="text-[10px] text-slate-400">⭐ = Ponto focal</div>
            </div>
            <div className="space-y-3">
              {pessoasOrdenadas.map((p) => (
                <div
                  key={p.id}
                  className={`rounded-xl p-3 border-2 ${p.ponto_focal ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
                  data-testid={`pessoa-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => togglePontoFocal(p)}
                          className={`transition ${p.ponto_focal ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
                          title={p.ponto_focal ? "Remover Ponto Focal" : "Marcar Ponto Focal"}
                          data-testid={`btn-pf-${p.id}`}
                        >
                          <Star className={`w-4 h-4 ${p.ponto_focal ? "fill-amber-400" : ""}`} />
                        </button>
                        <span className="truncate">{p.nome}</span>
                        {p.ponto_focal && (
                          <span className="text-[9px] uppercase font-bold tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded">PF</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-1">
                        <span>{p.funcao}</span>
                        {p.telefone && (
                          <>
                            <span>·</span>
                            <a
                              href={whatsappHref(p.telefone)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-700 hover:underline font-medium inline-flex items-center gap-0.5"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`funil-pessoa-tel-wa-${p.id}`}
                              title="Abrir no WhatsApp Web"
                            >
                              <MessageCircle className="w-3 h-3" /> {p.telefone}
                            </a>
                          </>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[9px] text-slate-500 uppercase font-bold">Receptividade:</span>
                        <StarRating value={p.estrelas || 0} onChange={(v) => setEstrelas(p, v)} testid={`stars-${p.id}`} />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button
                      onClick={() => setContatoModal({ pessoa: p, canal: "ligacao" })}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50 min-w-[110px]"
                      data-testid={`btn-ligacao-${p.id}`}
                    >
                      <Phone className="w-4 h-4 mr-1" /> Ligação
                    </Button>
                    <Button
                      onClick={() => setContatoModal({ pessoa: p, canal: "whatsapp" })}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 min-w-[110px]"
                      data-testid={`btn-whatsapp-${p.id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                    </Button>
                    <Button
                      onClick={() => setEnviarModal({ pessoa: p })}
                      variant="outline"
                      size="sm"
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                      data-testid={`btn-enviar-funil-${p.id}`}
                      title="Enviar esta pessoa para outro curso/estágio"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {pessoas.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-4">Nenhuma pessoa cadastrada. Clique em "Pessoa" para adicionar.</div>
              )}
            </div>
          </div>

          {/* Histórico */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Histórico de contatos ({contatos.length})
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {contatos.map((c) => {
                const p = pessoas.find((x) => x.id === c.pessoa_id);
                const d = new Date(c.data);
                return (
                  <div key={c.id} className="text-xs bg-slate-50 rounded-lg p-2 border border-slate-100">
                    <div className="flex justify-between">
                      <span className="font-semibold">
                        {c.canal === "ligacao" ? "📞 Ligação" : "💬 WhatsApp"} — {p?.nome || "?"}
                      </span>
                      <span className="text-slate-400">{d.toLocaleDateString("pt-BR")} {d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {c.observacao && <div className="text-slate-600 mt-1">{c.observacao}</div>}
                    {c.data_retorno && (
                      <div className="text-blue-700 font-semibold mt-1 flex items-center gap-1">
                        📅 Retorno: {new Date(c.data_retorno + "T12:00:00").toLocaleDateString("pt-BR")}
                        {c.retorno_concluido && <span className="text-emerald-600 ml-1">· concluído</span>}
                      </div>
                    )}
                  </div>
                );
              })}
              {contatos.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-4">Sem contatos ainda.</div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>

      {/* Nova pessoa modal */}
      <Dialog open={novaPessoaOpen} onOpenChange={setNovaPessoaOpen}>
        <DialogContent className="max-w-lg" data-testid="nova-pessoa-dialog">
          <DialogHeader><DialogTitle>Nova pessoa em {mun.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input value={newP.nome} onChange={(e) => setNewP({ ...newP, nome: e.target.value })} data-testid="nova-pessoa-nome" />
              </div>
              <div>
                <Label>Cargo</Label>
                <Select value={newP.funcao} onValueChange={(v) => setNewP({ ...newP, funcao: v })}>
                  <SelectTrigger data-testid="nova-pessoa-funcao"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Vereador", "Presidente", "Assessor", "Secretário", "Contador", "Servidor", "Outro"].map(f =>
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Partido</Label>
                <Input value={newP.partido} onChange={(e) => setNewP({ ...newP, partido: e.target.value })} placeholder="Ex.: PT, PL, PSD" data-testid="nova-pessoa-partido" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={newP.telefone} onChange={(e) => setNewP({ ...newP, telefone: e.target.value })} data-testid="nova-pessoa-telefone" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={newP.whatsapp} onChange={(e) => setNewP({ ...newP, whatsapp: e.target.value })} data-testid="nova-pessoa-whatsapp" />
              </div>
              <div className="col-span-2">
                <Label>E-mail</Label>
                <Input type="email" value={newP.email} onChange={(e) => setNewP({ ...newP, email: e.target.value })} data-testid="nova-pessoa-email" />
              </div>
              <div>
                <Label>Instagram</Label>
                <Input value={newP.instagram} onChange={(e) => setNewP({ ...newP, instagram: e.target.value })} placeholder="@usuario" data-testid="nova-pessoa-ig" />
              </div>
              <div>
                <Label>Data de nascimento</Label>
                <Input type="date" value={newP.data_nascimento} onChange={(e) => setNewP({ ...newP, data_nascimento: e.target.value })} data-testid="nova-pessoa-nasc" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaPessoaOpen(false)}>Cancelar</Button>
            <Button onClick={salvarNovaPessoa} data-testid="nova-pessoa-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal registrar contato */}
      <ContatoRegistrarModal
        open={!!contatoModal}
        onClose={() => setContatoModal(null)}
        pessoa={contatoModal?.pessoa}
        canal={contatoModal?.canal}
        municipioId={mun.id}
        cursoId={card?.curso_id}
        onSaved={onContatoSaved}
      />

      {/* Modal enviar UMA pessoa para curso/estágio */}
      {enviarModal && (
        <EnviarPessoaFunilModalMini
          open={!!enviarModal}
          onClose={() => setEnviarModal(null)}
          pessoa={enviarModal.pessoa}
          municipioId={mun.id}
          onDone={() => { setEnviarModal(null); loadPanel(); reload?.(); }}
        />
      )}
    </Sheet>
  );
}

function EnviarPessoaFunilModalMini({ open, onClose, pessoa, municipioId, onDone }) {
  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState("");
  const [estagio, setEstagio] = useState("incerto");

  useEffect(() => {
    if (!open) return;
    api.get("/cursos?status=ativo").then((r) => setCursos(r.data || [])).catch(() => {});
  }, [open]);

  const enviar = async () => {
    if (!cursoId) { toast.error("Escolha um curso"); return; }
    try {
      await api.post("/prospeccao/enviar-curso", {
        municipio_id: municipioId,
        curso_id: cursoId,
        atribuicoes: [{ pessoa_id: pessoa.id, estagio }],
      });
      toast.success(`${pessoa.nome} enviada(o) para o funil`);
      onDone?.();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro")); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="modal-enviar-pessoa-panel">
        <DialogHeader>
          <DialogTitle>Enviar {pessoa?.nome} para o funil</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Curso</Label>
            <Select value={cursoId} onValueChange={setCursoId}>
              <SelectTrigger data-testid="select-curso-mini"><SelectValue placeholder="Escolha um curso..." /></SelectTrigger>
              <SelectContent>
                {cursos.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.uf ? `${c.uf} · ` : ""}{c.nome} · {c.mes_ano}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Estágio inicial</Label>
            <Select value={estagio} onValueChange={setEstagio}>
              <SelectTrigger data-testid="select-estagio-mini"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="incerto">Incerto</SelectItem>
                <SelectItem value="interessado">Interessado</SelectItem>
                <SelectItem value="certo">Certo</SelectItem>
                <SelectItem value="nao_querem">Não quer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-enviar-pessoa-mini">
            <Send className="w-4 h-4 mr-1" /> Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Motivo de perda modal ---------- */
function MotivoPerdaModal({ open, onClose, onSave }) {
  const [motivos, setMotivos] = useState({});
  const [cat, setCat] = useState("");
  const [sub, setSub] = useState("");
  const [obs, setObs] = useState("");
  useEffect(() => { if (open) api.get("/motivos-perda").then((r) => setMotivos(r.data)); }, [open]);
  const salvar = () => {
    if (!cat || !sub) return toast.error("Selecione categoria e motivo");
    onSave({ categoria: cat, sub, obs });
    setCat(""); setSub(""); setObs("");
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="motivo-perda-dialog">
        <DialogHeader><DialogTitle>Por que este município não vai comprar?</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Categoria *</Label>
            <Select value={cat} onValueChange={(v) => { setCat(v); setSub(""); }}>
              <SelectTrigger data-testid="motivo-categoria"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {Object.keys(motivos).map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {cat && (
            <div>
              <Label>Motivo específico *</Label>
              <Select value={sub} onValueChange={setSub}>
                <SelectTrigger data-testid="motivo-sub"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {(motivos[cat] || []).map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Observação (opcional)</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} data-testid="motivo-obs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} className="bg-red-600 hover:bg-red-700" data-testid="motivo-salvar">Registrar Perda</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Editar Inscrição (Iter 42 · item #5) ----------
   Modal usado quando um card em estágio "Inscrito" é clicado. Permite editar
   qtd, valores e adicionar/remover pessoas. Quando NF já foi emitida, backend
   marca `alterada_apos_emissao=true` automaticamente.
*/
function EditarInscricaoModal({ open, onClose, card, mun, curso, onDone }) {
  const [inscricao, setInscricao] = useState(null);
  const [pessoasMun, setPessoasMun] = useState([]);
  const [qtd, setQtd] = useState(1);
  const [valorVendido, setValorVendido] = useState(0);
  const [valorCheio, setValorCheio] = useState(0);
  const [alunos, setAlunos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [respNf, setRespNf] = useState({ nome: "", email: "" });
  const [cort, setCort] = useState({ qtd: 0, motivo: "" });

  useEffect(() => {
    if (!open || !card) return;
    (async () => {
      // Busca a inscrição associada ao card
      const [rIns, rPessoas] = await Promise.all([
        api.get(`/inscricoes?card_id=${card.id}`),
        mun ? api.get(`/pessoas?municipio_id=${mun.id}`) : Promise.resolve({ data: [] }),
      ]);
      const insList = rIns.data || [];
      const insAtiva = insList.find((i) => !i.cancelada_em) || insList[0];
      setInscricao(insAtiva || null);
      setPessoasMun(rPessoas.data || []);
      if (insAtiva) {
        setQtd(insAtiva.quantidade || 1);
        setValorVendido(insAtiva.valor_vendido || 0);
        setValorCheio(insAtiva.valor_cheio || 0);
        setAlunos(insAtiva.alunos_pessoa_ids || []);
        setRespNf({ nome: insAtiva.nf_responsavel_nome || "", email: insAtiva.nf_responsavel_email || "" });
        setCort({ qtd: insAtiva.cortesias || 0, motivo: insAtiva.cortesia_motivo || "" });
      }
    })();
  }, [open, card, mun]);

  if (!open || !card) return null;

  const nfEmitida = inscricao?.nf_status === "emitida";
  const soCortesia = qtd > 0 && (Number(cort.qtd) || 0) >= qtd;
  const totalCheio = soCortesia ? 0 : Number(valorCheio || 0);
  const totalVendido = soCortesia ? 0 : Number(valorVendido || 0);
  const desconto = Math.max(0, totalCheio - totalVendido);

  const toggleAluno = (pid) => {
    setAlunos((cur) => cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]);
  };

  const salvar = async () => {
    if (!inscricao) { toast.error("Inscrição não encontrada"); return; }
    if (qtd <= 0) { toast.error("Quantidade deve ser positiva"); return; }
    const errCort = cortesiaErro(qtd, cort);
    if (errCort) { toast.error(errCort); return; }
    if (qtd - (Number(cort.qtd) || 0) > 0 && totalVendido <= 0) { toast.error("Valor negociado obrigatório"); return; }
    const respPreenchido = respNf.nome.trim() || respNf.email.trim();
    const viraPaga = inscricao.nf_status === "sem_nf" && !soCortesia;
    if ((respPreenchido || viraPaga) && respNfInvalido(respNf)) {
      toast.error("Informe o nome e um e-mail válido do responsável pela NF"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/inscricoes/${inscricao.id}`, {
        quantidade: qtd,
        valor_cheio: totalCheio,
        valor_vendido: totalVendido,
        cortesias: Number(cort.qtd) || 0,
        cortesia_motivo: cort.motivo.trim(),
        alunos_pessoa_ids: alunos,
        nf_responsavel_nome: respNf.nome.trim(),
        nf_responsavel_email: respNf.email.trim().toLowerCase(),
      });
      toast.success(nfEmitida
        ? "Inscrição alterada ✓ · NF marcada como 'alterada'"
        : "Inscrição alterada ✓");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao salvar"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="modal-editar-inscricao">
        <DialogHeader className="pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-600" />
            Editar inscrição · {mun?.nome}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">
            {curso?.nome} · {curso?.mes_ano}
            {nfEmitida && (
              <span className="ml-2 inline-flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-800 rounded px-1.5 py-0.5 text-[10px] uppercase font-bold" data-testid="edit-insc-nf-warn">
                ⚠️ NF já emitida — alterações marcarão a NF como "alterada"
              </span>
            )}
          </div>
        </DialogHeader>

        {!inscricao ? (
          <div className="py-10 text-center text-slate-500 text-sm">Carregando inscrição…</div>
        ) : (
          <div className="space-y-4 pt-3">
            {/* Valores */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Quantidade *</Label>
                <Input
                  type="number" min="1"
                  value={qtd}
                  onChange={(e) => setQtd(Number(e.target.value) || 0)}
                  data-testid="edit-insc-qtd"
                />
              </div>
              <div>
                <Label className="text-xs">Valor tabela (total)</Label>
                <Input
                  type="number" step="0.01"
                  value={soCortesia ? 0 : valorCheio}
                  disabled={soCortesia}
                  onChange={(e) => setValorCheio(e.target.value)}
                  data-testid="edit-insc-valor-cheio"
                />
              </div>
              <div>
                <Label className="text-xs">Valor negociado (total){soCortesia ? "" : " *"}</Label>
                <Input
                  type="number" step="0.01"
                  value={soCortesia ? 0 : valorVendido}
                  disabled={soCortesia}
                  onChange={(e) => setValorVendido(e.target.value)}
                  data-testid="edit-insc-valor-vendido"
                />
              </div>
            </div>
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 flex items-center gap-4 tabular-nums">
              <span>Desconto: <b className={desconto > 0 ? "text-red-600" : "text-slate-700"}>{formatBRL(desconto)}</b></span>
              <span>Ticket médio: <b>{formatBRL(qtd ? totalVendido / qtd : 0)}</b></span>
            </div>

            <CortesiaFields qtd={qtd} value={cort} onChange={setCort} />

            <ResponsavelNfFields value={respNf} onChange={setRespNf} pessoas={pessoasMun} required={inscricao.nf_status === "sem_nf" && !soCortesia} semNf={soCortesia} />

            {/* Alunos */}
            <div>
              <Label className="text-xs">Pessoas inscritas ({alunos.length}/{pessoasMun.length})</Label>
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded p-2 space-y-1 mt-1">
                {pessoasMun.length === 0 ? (
                  <div className="text-xs text-slate-400 py-3 text-center italic">Nenhuma pessoa vinculada ao município</div>
                ) : pessoasMun.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={alunos.includes(p.id)}
                      onChange={() => toggleAluno(p.id)}
                      data-testid={`edit-insc-aluno-${p.id}`}
                    />
                    <span className="flex-1 truncate">
                      <b>{p.nome}</b>
                      <span className="text-xs text-slate-500 ml-1">— {p.funcao}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Se remover pessoas, elas serão marcadas como excluídas no módulo Gestão de Cursos.
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving || !inscricao} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-insc-salvar">
            <Send className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Inscrição modal ---------- */
export function InscricaoModal({ open, onClose, onSave, card, mun }) {
  const [qtd, setQtd] = useState(1);
  const [unitario, setUnitario] = useState(0);
  const [fonte, setFonte] = useState("");
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [pessoas, setPessoas] = useState([]);
  const [selPessoas, setSelPessoas] = useState([]);
  const [novoOpen, setNovoOpen] = useState(false);
  const [novaP, setNovaP] = useState({ nome: "", funcao: "Vereador", telefone: "", partido: "", whatsapp: "", email: "" });
  const [cursoValorCheio, setCursoValorCheio] = useState(0);
  const [respNf, setRespNf] = useState({ nome: "", email: "" });
  const [existente, setExistente] = useState(null);
  const [cort, setCort] = useState({ qtd: 0, motivo: "" });
  const jaInscritos = existente?.alunos_pessoa_ids || [];
  const nQtd = Number(qtd) || 0;
  const pagantes = Math.max(0, nQtd - (Number(cort.qtd) || 0));

  useEffect(() => {
    if (open && mun && card) {
      setQtd(1); setUnitario(0); setFonte(""); setData(new Date().toISOString().slice(0, 10));
      setSelPessoas([]);
      setRespNf({ nome: "", email: "" });
      setCort({ qtd: 0, motivo: "" });
      setExistente(null);
      // Iter 80 · município já inscrito neste curso → abre como inscrição geral
      api.get(`/cards/${card.id}/inscricao-existente`).then((r) => {
        if (!r.data?.card_id) return;
        setExistente(r.data);
        setSelPessoas(r.data.alunos_pessoa_ids || []);
        setRespNf({ nome: r.data.nf_responsavel_nome || "", email: r.data.nf_responsavel_email || "" });
        toast.warning(`${mun.nome} já tem inscrição neste curso`, { description: "Abrimos a inscrição geral para juntar as novas pessoas com quem já está inscrito.", duration: 8000 });
      }).catch(() => {});
      api.get(`/pessoas?municipio_id=${mun.id}`).then((r) => setPessoas(r.data));
      // Busca o valor cheio do curso para cálculo automático
      if (card.curso_id) {
        api.get(`/cursos/${card.curso_id}`).then((r) => {
          setCursoValorCheio(Number(r.data?.valor_cheio || 0));
        }).catch(() => setCursoValorCheio(0));
      }
    }
  }, [open, mun?.id, card?.curso_id]);

  const togglePessoa = (pid) => {
    if (jaInscritos.includes(pid)) return;
    setSelPessoas((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      if (prev.length - jaInscritos.length >= qtd) {
        toast.info(`Você definiu ${qtd} ${existente ? "novos " : ""}inscritos. Ajuste a quantidade para adicionar mais.`);
        return prev;
      }
      return [...prev, pid];
    });
  };

  const criarNova = async () => {
    if (!novaP.nome.trim()) return toast.error("Informe o nome");
    try {
      const { data: nova } = await api.post("/pessoas", { ...novaP, municipio_id: mun.id });
      setPessoas((prev) => [...prev, nova]);
      setSelPessoas((prev) => prev.length < qtd ? [...prev, nova.id] : prev);
      setNovaP({ nome: "", funcao: "Vereador", telefone: "", partido: "", whatsapp: "", email: "" });
      setNovoOpen(false);
      toast.success("Aluno adicionado à seleção");
    } catch { toast.error("Erro"); }
  };

  const salvar = () => {
    if (!qtd || qtd <= 0) return toast.error("Informe a quantidade");
    const errCort = cortesiaErro(nQtd, cort);
    if (errCort) return toast.error(errCort);
    if (pagantes > 0 && (!unitario || unitario <= 0)) return toast.error("Informe o valor unitário de venda");
    if (!fonte) return toast.error("Selecione a fonte da inscrição");
    const respPreenchido = respNf.nome.trim() || respNf.email.trim();
    if ((pagantes > 0 || respPreenchido) && respNfInvalido(respNf)) return toast.error("Informe o nome e um e-mail válido do responsável pela NF");
    // Sistema calcula automaticamente sobre as vagas pagas: cheio = curso.valor_cheio × pagantes; vendido = unitario × pagantes
    const totalVendido = pagantes > 0 ? Number(unitario) * pagantes : 0;
    const totalCheio = cursoValorCheio > 0 ? cursoValorCheio * pagantes : totalVendido;
    onSave({
      card_id: card.id,
      quantidade: nQtd,
      valor_cheio: totalCheio,
      valor_vendido: totalVendido,
      cortesias: Number(cort.qtd) || 0,
      cortesia_motivo: cort.motivo.trim(),
      fonte,
      data: new Date(data + "T12:00:00Z").toISOString(),
      alunos_pessoa_ids: selPessoas,
      nf_responsavel_nome: respNf.nome.trim(),
      nf_responsavel_email: respNf.email.trim(),
      unificar_card_id: existente?.card_id || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inscricao-dialog">
        <DialogHeader><DialogTitle>{existente ? "Inscrição geral 🎉" : "Registrar Inscrição 🎉"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <InscricaoGeralAviso existente={existente} munNome={mun?.nome} novosQtd={qtd} novosValor={(Number(unitario) || 0) * pagantes} />
          <div>
            <Label>{existente ? "Quantidade de novos inscritos *" : "Quantidade de inscritos *"}</Label>
            <Input type="number" min={1} value={qtd} onChange={(e) => setQtd(e.target.value)} data-testid="insc-qtd" />
          </div>
          <CortesiaFields qtd={nQtd} value={cort} onChange={setCort} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Valor unitário de venda (R$) {pagantes > 0 ? "*" : "· só cortesia"}</Label>
              <Input
                type="number"
                step="0.01"
                value={pagantes > 0 ? unitario : 0}
                disabled={pagantes === 0}
                onChange={(e) => setUnitario(e.target.value)}
                data-testid="insc-unitario"
                placeholder="Ex: 850.00"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                {cursoValorCheio > 0 ? (
                  <>Valor cheio do curso: <b>{formatBRL(cursoValorCheio)}</b> · Total cheio: <b>{formatBRL(cursoValorCheio * pagantes)}</b></>
                ) : (
                  <>Sem valor cheio cadastrado no curso — total cheio = total vendido.</>
                )}
              </div>
            </div>
            <div>
              <Label>Total vendido (auto)</Label>
              <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm flex items-center font-bold tabular-nums" data-testid="insc-total-vendido">
                {formatBRL((Number(unitario) || 0) * pagantes)}
              </div>
            </div>
          </div>
          <div>
            <Label>Fonte da inscrição *</Label>
            <select
              value={fonte}
              onChange={(e) => setFonte(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="insc-fonte"
            >
              <option value="">Selecione a fonte…</option>
              <option value="prospeccao_ativa">Prospecção Ativa</option>
              <option value="acao_conecta">Ação Conecta</option>
              <option value="trafego_pago">Tráfego Pago</option>
              <option value="site">Site</option>
              <option value="email_mkt">E-mail MKT</option>
              <option value="app">App</option>
            </select>
          </div>
          <div>
            <Label>Data</Label>
            <Input type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </div>

          <ResponsavelNfFields value={respNf} onChange={setRespNf} pessoas={pessoas} required={pagantes > 0} semNf={pagantes === 0} />

          {/* Seleção de alunos (pessoas do município) */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">
                Alunos ({selPessoas.length}/{(Number(qtd) || 0) + jaInscritos.length})
              </Label>
              <button
                type="button"
                onClick={() => setNovoOpen(true)}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                data-testid="insc-add-pessoa"
              >
                <Plus className="w-3 h-3" /> Novo aluno
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="insc-pessoas-list">
              {pessoas.length === 0 && (
                <div className="text-xs text-slate-400 p-3 text-center">Nenhuma pessoa cadastrada neste município. Clique em "Novo aluno".</div>
              )}
              {pessoas.map((p) => {
                const on = selPessoas.includes(p.id);
                return (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${on ? "bg-blue-50" : ""}`}
                    data-testid={`insc-pessoa-${p.id}`}
                  >
                    <input type="checkbox" checked={on} disabled={jaInscritos.includes(p.id)} onChange={() => togglePessoa(p.id)} className="w-4 h-4 accent-blue-600" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.nome} {p.partido && <span className="text-xs text-slate-400">({p.partido})</span>}</div>
                      <div className="text-xs text-slate-500">{p.funcao}{jaInscritos.includes(p.id) && <span className="ml-1 text-blue-700 font-bold">· já inscrito</span>}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {selPessoas.length - jaInscritos.length < qtd && qtd > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                ⚠ Faltam {qtd - (selPessoas.length - jaInscritos.length)} aluno(s). Você pode salvar assim mesmo — os alunos podem ser vinculados depois.
              </div>
            )}
          </div>

          {pagantes > 0 && unitario > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm space-y-0.5">
              <div>Total vendido: <b>{formatBRL(Number(unitario) * pagantes)}</b></div>
              {cursoValorCheio > 0 && (
                <div>Desconto total: <b>{formatBRL(Math.max(0, (cursoValorCheio - Number(unitario)) * pagantes))}</b> ({Math.round((1 - Number(unitario) / cursoValorCheio) * 100)}% off)</div>
              )}
              <div>Ticket médio: <b>{formatBRL(Number(unitario))}</b></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} className="bg-emerald-600 hover:bg-emerald-700" data-testid="insc-salvar">
            Fechar Venda 🎯
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Novo aluno rápido */}
      <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
        <DialogContent data-testid="insc-novo-aluno-dialog">
          <DialogHeader><DialogTitle>Novo aluno em {mun?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={novaP.nome} onChange={(e) => setNovaP({ ...novaP, nome: e.target.value })} data-testid="insc-novo-nome" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cargo</Label>
                <Select value={novaP.funcao} onValueChange={(v) => setNovaP({ ...novaP, funcao: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Vereador", "Presidente", "Assessor", "Secretário", "Contador", "Servidor", "Outro"].map(f =>
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Partido</Label><Input value={novaP.partido} onChange={(e) => setNovaP({ ...novaP, partido: e.target.value })} /></div>
              <div><Label>Telefone</Label><Input value={novaP.telefone} onChange={(e) => setNovaP({ ...novaP, telefone: e.target.value })} /></div>
              <div><Label>WhatsApp</Label><Input value={novaP.whatsapp} onChange={(e) => setNovaP({ ...novaP, whatsapp: e.target.value })} /></div>
              <div className="col-span-2"><Label>E-mail</Label><Input type="email" value={novaP.email} onChange={(e) => setNovaP({ ...novaP, email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoOpen(false)}>Cancelar</Button>
            <Button onClick={criarNova} data-testid="insc-novo-salvar">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/* ---------- Main Page ---------- */
export default function FunilBoard() {
  const { user } = useAuth();
  const { cursoId } = useParams();
  const nav = useNavigate();

  const [cursos, setCursos] = useState([]);
  const [curso, setCurso] = useState(null);
  const [cards, setCards] = useState([]);
  const [muns, setMuns] = useState([]);
  const [users, setUsers] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);
  const [medals, setMedals] = useState(null);
  const [filtroVend, setFiltroVend] = useState("todos");
  const [filtroUF, setFiltroUF] = useState("todos"); // Iter 40 · UF dos municípios (cards)
  const [filtroEstadoCurso, setFiltroEstadoCurso] = useState("todos"); // Iter 70 · UF do curso
  const [filtroBusca, setFiltroBusca] = useState("");
  // Iter 68 · Vendedora pode alternar entre "só meus" e "ver todos" (cards de colegas em read-only translúcido)
  const [verTodos, setVerTodos] = useState(false);
  // Iter 70 · "Ver todos" mostra tudo do curso, independente do estado dos municípios
  useEffect(() => { if (verTodos) setFiltroUF("todos"); }, [verTodos]);
  // Iter 42: mês seguido pela URL para que a "Minha meta do mês" acompanhe
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtroMes, setFiltroMes] = useState(searchParams.get("mes") || new Date().toISOString().slice(0, 7));
  const [activeCardId, setActiveCardId] = useState(null);
  const [openCard, setOpenCard] = useState(null);
  const [motivoPending, setMotivoPending] = useState(null); // {cardId}
  const [inscricaoCard, setInscricaoCard] = useState(null);
  const [inscricaoPayload, setInscricaoPayload] = useState(null);  // payload staged para confirmação de e-mail
  // Iter 42: receita por card (chip $ na coluna Inscritos/Certos)
  const [receitaPorCard, setReceitaPorCard] = useState({});
  // Iter 42: editar inscrição a partir do card Inscrito
  const [editandoInsc, setEditandoInsc] = useState(null); // { card }

  // Sync mês → URL
  useEffect(() => {
    const q = new URLSearchParams(searchParams);
    if (filtroMes) q.set("mes", filtroMes); else q.delete("mes");
    setSearchParams(q, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroMes]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  // Load cursos & auto-select (do mês vigente por padrão)
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/cursos?status=ativo");
      setCursos(data);
      if (!cursoId && data.length > 0) {
        // prioriza curso do mês vigente
        const doMes = data.find((c) => c.mes_ano === filtroMes);
        nav(`/funil/${(doMes || data[0]).id}${filtroMes ? `?mes=${filtroMes}` : ""}`, { replace: true });
      }
    })();
  }, [cursoId, nav, filtroMes]);

  // Formatação amigável do curso: "UF · Nome · Data"
  const formatCursoLabel = (c) => {
    if (!c) return "";
    const parts = [];
    if (c.uf) parts.push(c.uf);
    parts.push(c.nome);
    if (c.data_realizacao) parts.push(c.data_realizacao);
    return parts.join(" · ");
  };

  // Meses disponíveis (dedup) — mês vigente sempre presente
  const mesesDisponiveis = useMemo(() => {
    const set = new Set(cursos.map((c) => c.mes_ano).filter(Boolean));
    set.add(new Date().toISOString().slice(0, 7));
    return Array.from(set).sort().reverse();
  }, [cursos]);

  // Iter 70: filtro de estado do topo filtra só a lista de CURSOS (uf do curso)
  const cursosDoMes = useMemo(() => {
    const doMes = cursos.filter((c) => c.mes_ano === filtroMes
      && (filtroEstadoCurso === "todos" || c.uf === filtroEstadoCurso));
    const atual = cursos.find((c) => c.id === cursoId);
    return atual && !doMes.some((c) => c.id === cursoId) ? [atual, ...doMes] : doMes;
  }, [cursos, filtroMes, cursoId, filtroEstadoCurso]);
  const estadosDosCursos = useMemo(() => [...new Set(cursos
    .filter((c) => c.mes_ano === filtroMes).map((c) => c.uf).filter(Boolean))].sort(), [cursos, filtroMes]);

  // Ao mudar o mês, redireciona para um curso do mês (se o atual não é do mês)
  useEffect(() => {
    if (!curso || !cursos.length) return;
    if (curso.mes_ano === filtroMes) return;
    const doMes = cursos.find((c) => c.mes_ano === filtroMes);
    if (doMes) nav(`/funil/${doMes.id}${filtroMes ? `?mes=${filtroMes}` : ""}`, { replace: true });
  }, [filtroMes, curso, cursos, nav]);

  const formatMes = (mes) => {
    if (!mes) return "";
    const [y, m] = mes.split("-");
    const nomes = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
    return `${nomes[Number(m)-1]}/${y}`;
  };

  const loadAll = useCallback(async () => {
    if (!cursoId) return;
    // Iter 68 · verTodos=true pede cards de todas vendedoras (só efetivo quando user.role=vendedora)
    const cardsUrl = `/cards?curso_id=${cursoId}${verTodos && user?.role === "vendedora" ? "&incluir_outras=true" : ""}`;
    const [c, cds, m, us, inscs, md] = await Promise.all([
      api.get(`/cursos/${cursoId}`),
      api.get(cardsUrl),
      api.get("/municipios"),
      api.get(user?.role === "vendedora" ? "/funil/colegas" : "/users"),
      api.get(`/inscricoes?curso_id=${cursoId}`),
      api.get("/medals").catch(() => ({ data: null })),
    ]);
    setCurso(c.data); setCards(cds.data); setMuns(m.data); setUsers(us.data); setInscricoes(inscs.data);
    setMedals(md.data);
  }, [cursoId, verTodos, user?.role]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Safety: close any open sheet/modal on unmount to help Radix portal cleanup
  useEffect(() => {
    return () => {
      setOpenCard(null);
      setMotivoPending(null);
      setInscricaoCard(null);
    };
  }, []);

  const { lastEvent } = useRealtime() || {};
  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === "card_moved" && lastEvent.curso_id === cursoId) loadAll();
    if (lastEvent.event === "inscricao") loadAll();
  }, [lastEvent, cursoId, loadAll]);

  // Iter 40: mapas úteis para KanbanCard
  const inscricoesMap = useMemo(() => {
    const m = {};
    (inscricoes || []).forEach((i) => { m[i.card_id] = (m[i.card_id] || 0) + (i.quantidade || 0); });
    return m;
  }, [inscricoes]);
  const cortesiasMap = useMemo(() => {
    const m = {};
    (inscricoes || []).forEach((i) => { if (i.cortesias) m[i.card_id] = (m[i.card_id] || 0) + i.cortesias; });
    return m;
  }, [inscricoes]);

  // Iter 42: receita agregada por card (chip $ nas colunas Certos/Inscritos)
  useEffect(() => {
    if (!cursoId) { setReceitaPorCard({}); return; }
    api.get(`/funil/receita-por-card?curso_id=${cursoId}`)
      .then((r) => setReceitaPorCard(r.data || {}))
      .catch(() => setReceitaPorCard({}));
  }, [cursoId, inscricoes, cards]);

  const confirmacoesMap = useMemo(() => {
    const m = {};
    (inscricoes || []).forEach((i) => {
      if (i.email_confirmacao?.enviado || (i.confirmacoes_enviadas || []).length > 0) {
        m[i.card_id] = true;
      }
    });
    return m;
  }, [inscricoes]);

  // Propostas geradas: tracked by presence of card.proposta_gerada_em (backend field, if any)
  const propostasMap = useMemo(() => {
    const m = {};
    (cards || []).forEach((c) => {
      if (c.proposta_gerada_em) m[c.id] = true;
    });
    return m;
  }, [cards]);

  const filtered = useMemo(() => cards.filter((c) => {
    if (filtroVend !== "todos" && c.vendedora_id !== filtroVend) return false;
    if (filtroUF !== "todos") {
      const m = muns.find((x) => x.id === c.municipio_id);
      if (m?.uf !== filtroUF) return false;
    }
    if (filtroBusca) {
      const m = muns.find((x) => x.id === c.municipio_id);
      if (!m?.nome.toLowerCase().includes(filtroBusca.toLowerCase())) return false;
    }
    return true;
  }), [cards, filtroVend, filtroUF, filtroBusca, muns]);

  const ufsDisponiveis = useMemo(() => {
    const s = new Set(cards.map((c) => muns.find((m) => m.id === c.municipio_id)?.uf).filter(Boolean));
    return Array.from(s).sort();
  }, [cards, muns]);

  const byCol = useMemo(() => {
    const grp = {};
    ESTAGIOS.forEach((e) => (grp[e.key] = []));
    filtered.forEach((c) => grp[c.estagio]?.push(c));
    return grp;
  }, [filtered]);

  // Header metrics
  const metrics = useMemo(() => {
    const realizado = inscricoes.reduce((s, i) => s + i.valor_vendido, 0);
    const inscritos = inscricoes.reduce((s, i) => s + i.quantidade, 0);
    const ticket = inscritos ? realizado / inscritos : 0;
    const certos = filtered.filter((c) => c.estagio === "certo");
    const exp_min = realizado + certos.reduce((s, c) => s + c.exp_min * (ticket || 0), 0);
    const exp_max = realizado + certos.reduce((s, c) => s + c.exp_max * (ticket || 0), 0);
    return { realizado, inscritos, ticket, exp_min, exp_max };
  }, [inscricoes, filtered]);

  // Iter 72: total de municípios distintos nas colunas do funil (acompanha os filtros)
  const totalMunicipios = useMemo(() => new Set(
    ESTAGIOS.flatMap((e) => byCol[e.key] || []).map((c) => c.municipio_id)
  ).size, [byCol]);

  // Métricas por coluna do funil (topo de cada fase)
  const colMetrics = useMemo(() => {
    const t = metrics.ticket || 0;
    const cardsCol = (k) => filtered.filter((c) => c.estagio === k);
    // Iter 68: soma as inscrições dos próprios cards da coluna (mesma fonte do chip do card)
    const inscritosIds = new Set(cardsCol("inscrito").map((c) => c.id));
    const inscDaColuna = inscricoes.filter((i) => inscritosIds.has(i.card_id));
    const inscValor = inscDaColuna.reduce((s, i) => s + (i.valor_vendido || 0), 0);
    const inscQtd = inscDaColuna.reduce((s, i) => s + (i.quantidade || 0), 0);

    const certosArr = cardsCol("certo");
    const certosQtd = certosArr.reduce((s, c) => s + (c.exp_max || c.exp_min || 0), 0);
    // Iter 58.7: usa a MESMA fonte que os cards (exp_max × valor_cheio do curso)
    // em vez de qtd × ticket_medio — o ticket reflete descontos históricos e
    // divergia da soma dos cards mostrada por card em cada município.
    const certosReceita = certosArr.reduce(
      (s, c) => s + (receitaPorCard[c.id]?.exp_max_receita || 0),
      0
    );

    const interessadosArr = cardsCol("interessado");
    const interessadosQtd = interessadosArr.reduce((s, c) => s + (c.exp_max || c.exp_min || 0), 0);

    const incertosArr = cardsCol("incerto");
    const incertosQtd = incertosArr.reduce((s, c) => s + (c.exp_max || c.exp_min || 0), 0);

    const nqArr = cardsCol("nao_querem");
    const nqQtd = nqArr.reduce((s, c) => s + (c.exp_max || c.exp_min || 0), 0);

    return {
      inscrito: {
        leftLabel: "Receita", left: formatBRL(inscValor),
        rightLabel: "Inscritos", right: inscQtd,
      },
      certo: {
        leftLabel: "Receita", left: formatBRL(certosReceita),
        rightLabel: "Inscritos", right: certosQtd,
      },
      interessado: {
        leftLabel: "Municípios", left: interessadosArr.length,
        rightLabel: "Inscritos", right: interessadosQtd,
      },
      incerto: {
        leftLabel: "Municípios", left: incertosArr.length,
        rightLabel: "Inscritos", right: incertosQtd,
      },
      nao_querem: {
        leftLabel: "Municípios", left: nqArr.length,
        rightLabel: "Negativas", right: nqQtd,
      },
    };
  }, [filtered, inscricoes, metrics.ticket, receitaPorCard]);

  // Drag & drop
  const handleDragEnd = async (evt) => {
    setActiveCardId(null);
    const { active, over } = evt;
    if (!over) return;
    const card = cards.find((c) => c.id === active.id);
    if (!card || card.estagio === over.id) return;
    // Guarda: vendedora só pode mover cards dela
    if (user?.role === "vendedora" && card.vendedora_id !== user.id) {
      toast.error("Você só pode mover os cards da sua carteira");
      return;
    }
    if (over.id === "nao_querem") {
      setMotivoPending({ cardId: card.id });
      return;
    }
    if (over.id === "inscrito") {
      // Ao arrastar para "Inscrito", abre o modal de registrar inscrição.
      // A movimentação do card acontece SÓ depois que a inscrição é salva
      // (via POST /inscricoes, que atualiza o estágio no backend).
      setInscricaoCard(card);
      return;
    }
    await moveCard(card.id, over.id);
  };

  const moveCard = async (cardId, novoEstagio, motivo = null) => {
    try {
      const body = { estagio: novoEstagio };
      if (motivo) {
        body.motivo_perda_categoria = motivo.categoria;
        body.motivo_perda_sub = motivo.sub;
        body.motivo_perda_obs = motivo.obs;
      }
      const { data } = await api.patch(`/cards/${cardId}`, body);
      setCards((prev) => prev.map((c) => c.id === cardId ? data : c));
      toast.success("Card movido");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao mover"));
    }
  };

  const adjustExp = async (card, kind, delta) => {
    const key = kind === "min" ? "exp_min" : "exp_max";
    const novoVal = Math.max(0, (card[key] || 0) + delta);
    try {
      const { data } = await api.patch(`/cards/${card.id}`, { [key]: novoVal });
      setCards((prev) => prev.map((c) => c.id === card.id ? data : c));
    } catch { toast.error("Erro"); }
  };

  // Iter 40: staged confirmação para abrir modal após inscrição
  const [confirmacaoIdParaEnviar, setConfirmacaoIdParaEnviar] = useState(null);

  const salvarInscricao = async (payload) => {
    try {
      const r = await api.post("/inscricoes", payload);
      toast.success(r?.data?.unificada_em ? "🎉 Inscrição geral atualizada · 1 card com todos os inscritos" : "🎉 Inscrição fechada!");
      setInscricaoCard(null);
      // Iter 40: pergunta imediatamente se quer enviar a confirmação
      const enviarAgora = window.confirm(
        r?.data?.unificada_em
          ? "Deseja enviar a NOVA confirmação de inscrição (com todos os inscritos) por e-mail agora?\n\nOK = Enviar agora · Cancelar = Enviar depois pelo perfil do município"
          : "Deseja enviar a confirmação de inscrição por e-mail agora?\n\nOK = Enviar agora · Cancelar = Enviar depois pelo perfil do município"
      );
      if (enviarAgora && r?.data?.id) {
        setConfirmacaoIdParaEnviar(r.data.id);
      }
      loadAll();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro")); }
  };

  // Iter 36: Cancelar inscrição direto do card no funil (estilo b — card fica em inscrito com tarja vermelha)
  const handleCancelarCard = async (card) => {
    const motivo = window.prompt(
      `Cancelar inscrição de "${muns.find((m) => m.id === card.municipio_id)?.nome || "município"}"?\n\nDigite o motivo do cancelamento:`
    );
    if (motivo === null) return; // clicou Cancelar
    if (!motivo.trim()) {
      toast.error("O motivo é obrigatório");
      return;
    }
    try {
      // Descobre o iid da inscrição ativa deste card
      const insc = inscricoes.find((i) => i.card_id === card.id);
      if (!insc) {
        toast.error("Inscrição não encontrada para este card");
        return;
      }
      const r = await api.delete(`/inscricoes/${insc.id}?motivo=${encodeURIComponent(motivo.trim())}&todas_do_card=true`);
      const nfs = r.data?.nfs_a_cancelar || [];
      toast.success("Inscrição cancelada · Dashboard atualizado", nfs.length ? { description: `O Fiscal foi avisado para cancelar a NF nº ${nfs.join(", ")}.`, duration: 8000 } : undefined);
      loadAll();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao cancelar"));
    }
  };

  const finalizarInscricao = async (emailOpts) => {
    if (!inscricaoPayload) return;
    try {
      const full = { ...inscricaoPayload, ...emailOpts };
      const res = await api.post("/inscricoes", full);
      const emailStatus = res?.data?.email_confirmacao;
      if (emailOpts?.enviar_email && emailStatus?.enviado) {
        toast.success("🎉 Inscrição fechada e e-mail de confirmação enviado!");
      } else if (emailOpts?.enviar_email && emailStatus && !emailStatus.enviado) {
        // Iter 61 · Mensagem clara + persistente quando o Resend bloqueia o destinatário
        const err = emailStatus.erro || "erro desconhecido";
        const isBlocked = err.includes("não entregável") || err.includes("bloqueou") || err.includes("manualmente");
        toast.warning(`⚠️ Inscrição salva, mas o e-mail não foi enviado: ${err}`, {
          duration: isBlocked ? 15000 : 8000,
          description: isBlocked ? "Abra o seu Gmail/Outlook e envie o e-mail direto da sua conta — o destinatário aceita e-mails pessoais sem problema." : undefined,
        });
      } else {
        toast.success("🎉 Inscrição fechada!");
      }
      setInscricaoPayload(null);
      loadAll();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro")); }
  };

  const salvarMotivoPerda = async (motivo) => {
    await moveCard(motivoPending.cardId, "nao_querem", motivo);
    setMotivoPending(null);
  };

  // Iter 68 · Guard: vendedora NÃO pode abrir detalhe de card de outra vendedora (apenas visualiza no funil)
  const isVendedora = user?.role === "vendedora";
  const isAlheio = useCallback((card) => isVendedora && card.vendedora_id !== user?.id, [isVendedora, user?.id]);
  const openCardSafe = useCallback((card) => {
    if (isAlheio(card)) {
      const vendDona = users.find((u) => u.id === card.vendedora_id);
      toast.error(
        `Este município não faz parte da sua carteira (é da ${vendDona?.nome || "outra vendedora"}).`,
        { duration: 6000, description: "Para acessar, peça à Diretoria para transferir pra sua carteira." }
      );
      return;
    }
    setOpenCard(card);
  }, [isAlheio, users]);
  const openEditInscSafe = useCallback((card) => {
    if (isAlheio(card)) {
      const vendDona = users.find((u) => u.id === card.vendedora_id);
      toast.error(`Este município não faz parte da sua carteira (é da ${vendDona?.nome || "outra vendedora"}).`);
      return;
    }
    setEditandoInsc({ card });
  }, [isAlheio, users]);

  const activeCard = cards.find((c) => c.id === activeCardId);
  const activeMun = activeCard && muns.find((m) => m.id === activeCard.municipio_id);

  if (!curso) return <div className="text-slate-500">Carregando funil...</div>;

  const pct = curso.meta_receita ? (metrics.realizado / curso.meta_receita) * 100 : 0;
  const vendedoras = users.filter((u) => u.role === "vendedora");

  return (
    <div className="space-y-4" data-testid="funil-board">
      {/* Header */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-600">
              {curso.uf && <span className="mr-1">{curso.uf}</span>}
              {curso.mes_ano} · {curso.data_realizacao}
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 truncate">{curso.nome}</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={filtroMes} onValueChange={setFiltroMes}>
              <SelectTrigger className="w-36" data-testid="select-mes"><SelectValue /></SelectTrigger>
              <SelectContent>
                {mesesDisponiveis.map((m) => (
                  <SelectItem key={m} value={m}>{formatMes(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroEstadoCurso} onValueChange={setFiltroEstadoCurso}>
              <SelectTrigger className="w-36" data-testid="select-uf-header" title="Filtra a lista de cursos pelo estado do curso"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Cursos: todos</SelectItem>
                {estadosDosCursos.map((uf) => (
                  <SelectItem key={uf} value={uf}>Cursos {uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={cursoId} onValueChange={(v) => nav(`/funil/${v}${filtroMes ? `?mes=${filtroMes}` : ""}`)}>
              <SelectTrigger className="w-72 md:w-96" data-testid="select-curso"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cursosDoMes.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-400">Nenhum curso ativo em {formatMes(filtroMes)}</div>
                )}
                {cursosDoMes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{formatCursoLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Iter 68 · Vendedora pode ver o funil geral do curso (translúcido/read-only) */}
            {user?.role === "vendedora" && (
              <Button
                variant={verTodos ? "default" : "outline"}
                onClick={() => setVerTodos((v) => !v)}
                data-testid="btn-ver-todos-funil"
                className={verTodos
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : "border-purple-300 text-purple-700 hover:bg-purple-50"
                }
                title={verTodos ? "Ocultar cards das colegas" : "Ver todos os municípios do curso (incluindo das colegas, read-only)"}
              >
                {verTodos ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {verTodos ? "Só meus" : "Ver todos"}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">
          <div>
            <div className="text-xs text-slate-500 uppercase font-bold">Meta</div>
            <div className="font-display text-xl font-bold tabular-nums">{formatBRL(curso.meta_receita)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-bold">Realizado</div>
            <div className="font-display text-xl font-bold tabular-nums text-emerald-600">{formatBRL(metrics.realizado)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-bold">Inscritos</div>
            <div className="font-display text-xl font-bold tabular-nums">{metrics.inscritos} / {curso.meta_inscritos}</div>
          </div>
          <div title="Total de municípios que estão nas colunas do funil deste curso">
            <div className="text-xs text-slate-500 uppercase font-bold">Nº de municípios</div>
            <div className="font-display text-xl font-bold tabular-nums text-blue-700" data-testid="metric-total-municipios">{totalMunicipios}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-bold">Ticket</div>
            <div className="font-display text-xl font-bold tabular-nums">{formatBRL(metrics.ticket)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-bold">Expect. fechamento</div>
            <div className="font-display text-sm font-bold tabular-nums">
              {formatBRL(metrics.exp_min)} <span className="text-slate-400">a</span> {formatBRL(metrics.exp_max)}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-500 font-semibold">Progresso: {pct.toFixed(0)}%</span>
          </div>
          <div className="progress-track h-3">
            <div className={`progress-fill ${pct >= 100 ? "win" : pct < 40 ? "warn" : ""}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col md:flex-row gap-2 mt-4">
          <Input
            placeholder="🔍 Buscar município..."
            value={filtroBusca}
            onChange={(e) => setFiltroBusca(e.target.value)}
            className="md:w-64"
            data-testid="filter-busca"
          />
          <Select
            value={filtroVend}
            onValueChange={(v) => {
              setFiltroVend(v);
              // Iter 69 · Vendedora escolhe uma colega → auto-ativa Ver Todos pra puxar cards dela
              if (user?.role === "vendedora" && v !== "todos" && v !== user.id) {
                setVerTodos(true);
              }
            }}
          >
            <SelectTrigger className="md:w-56" data-testid="filter-vendedora"><SelectValue placeholder="Vendedora" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">
                {user?.role === "vendedora" ? "👥 Todas (eu + colegas)" : "Todas as vendedoras"}
              </SelectItem>
              {user?.role === "vendedora" && (
                <SelectItem value={user.id}>👤 Só minhas</SelectItem>
              )}
              {vendedoras
                .filter((v) => v.id !== user?.id)
                .map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {user?.role === "vendedora" ? "📊 " : ""}{v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                    {v.nucleo_nome ? ` · ${v.nucleo_nome}` : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select value={filtroUF} onValueChange={setFiltroUF}>
            <SelectTrigger className="md:w-32" data-testid="filter-uf"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os estados</SelectItem>
              {ufsDisponiveis.map((uf) => (
                <SelectItem key={uf} value={uf}>{uf}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Iter 69 · Banner de benchmark: vendedora olhando funil de uma colega específica */}
        {user?.role === "vendedora" && filtroVend !== "todos" && filtroVend !== user?.id && (
          <div className="mt-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg px-4 py-2.5 flex items-center gap-2.5 text-sm" data-testid="banner-benchmark">
            <TrendingUp className="w-4 h-4 text-purple-700 shrink-0" />
            <span className="text-slate-700">
              <strong className="text-purple-900">Modo benchmark:</strong> você está vendo apenas o funil de{" "}
              <strong className="text-purple-900">
                {users.find((u) => u.id === filtroVend)?.nome || "colega"}
              </strong>
              {" "}· {cards.filter((c) => c.vendedora_id === filtroVend).length} municípios · read-only
            </span>
            <button
              onClick={() => setFiltroVend(user.id)}
              className="ml-auto text-xs font-semibold text-purple-700 hover:text-purple-900 underline shrink-0"
              data-testid="btn-sair-benchmark"
            >
              Voltar pro meu funil
            </button>
          </div>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveCardId(e.active.id)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveCardId(null)}
      >
        <div className="kanban-board">
          {ESTAGIOS.map((e) => (
            <KanbanColumn
              key={e.key}
              estagio={e}
              cards={byCol[e.key] || []}
              muns={muns}
              users={users}
              onOpen={openCardSafe}
              onExp={adjustExp}
              isVendedora={user?.role === "vendedora"}
              currentUserId={user?.id}
              medals={medals}
              colMetrics={colMetrics[e.key]}
              onCancelar={e.key === "inscrito" ? handleCancelarCard : undefined}
              inscricoesMap={inscricoesMap}
              cortesiasMap={cortesiasMap}
              receitaMap={receitaPorCard}
              propostasMap={propostasMap}
              confirmacoesMap={confirmacoesMap}
              onOpenEditInsc={openEditInscSafe}
            />
          ))}
        </div>
        <DragOverlay>
          {activeCard && activeMun && (
            <div className="bg-white rounded-lg border border-blue-400 shadow-xl p-3 w-64 rotate-2">
              <div className="font-semibold">{activeMun.nome}</div>
              <div className="text-xs text-slate-500">{activeMun.uf}</div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Side Panel */}
      <SidePanel
        open={!!openCard}
        onClose={() => setOpenCard(null)}
        card={openCard}
        mun={openCard && muns.find((m) => m.id === openCard.municipio_id)}
        curso={curso}
        onInscricao={(c) => { setInscricaoCard(c); }}
        onContato={loadAll}
        reload={loadAll}
      />

      <MotivoPerdaModal
        open={!!motivoPending}
        onClose={() => setMotivoPending(null)}
        onSave={salvarMotivoPerda}
      />
      <InscricaoModal
        open={!!inscricaoCard}
        onClose={() => setInscricaoCard(null)}
        onSave={salvarInscricao}
        card={inscricaoCard}
        mun={inscricaoCard && muns.find((m) => m.id === inscricaoCard.municipio_id)}
      />
      {/* Iter 42 (item #5): editar inscrição a partir do card Inscrito */}
      <EditarInscricaoModal
        open={!!editandoInsc}
        onClose={() => setEditandoInsc(null)}
        card={editandoInsc?.card}
        mun={editandoInsc && muns.find((m) => m.id === editandoInsc.card.municipio_id)}
        curso={curso}
        onDone={loadAll}
      />
      {/* Iter 40: Modal para enviar confirmação logo após fechar a inscrição */}
      {confirmacaoIdParaEnviar && (() => {
        // Precisa buscar dados atualizados do inscricao criado
        const ConfirmacaoEmailWrapper = () => {
          const [ins, setIns] = useState(null);
          useEffect(() => {
            api.get(`/inscricoes`).then((r) => {
              const found = (r.data || []).find((x) => x.id === confirmacaoIdParaEnviar);
              if (found) setIns(found);
            });
          }, []);
          if (!ins) return null;
          return (
            <ConfirmacaoEmailModal
              open={true}
              onClose={() => setConfirmacaoIdParaEnviar(null)}
              onConfirm={async (emailOpts) => {
                // Iter 61 · "Salvar sem enviar e-mail" → não chama o backend
                if (emailOpts?.enviar_email === false) {
                  toast.success("✉️ Inscrição mantida sem envio de e-mail.");
                  setConfirmacaoIdParaEnviar(null);
                  return;
                }
                try {
                  await api.post(`/inscricoes/${confirmacaoIdParaEnviar}/enviar-confirmacao`, emailOpts);
                  toast.success("✉️ Confirmação de inscrição enviada!");
                  setConfirmacaoIdParaEnviar(null);
                  loadAll();
                } catch (e) {
                  const msg = apiErrorMsg(e, "Falha ao enviar");
                  const isBlocked = msg.includes("não entregável") || msg.includes("bloqueou") || msg.includes("manualmente");
                  toast.error(msg, {
                    duration: isBlocked ? 15000 : 6000,
                    description: isBlocked ? "Abra o seu Gmail/Outlook e envie o e-mail direto da sua conta — o destinatário aceita e-mails pessoais sem problema." : undefined,
                  });
                }
              }}
              previewPayload={{
                card_id: ins.card_id,
                quantidade: ins.quantidade || 1,
                valor_vendido: ins.valor_vendido || 0,
                valor_cheio: ins.valor_cheio || 0,
                alunos_pessoa_ids: ins.alunos_pessoa_ids || [],
              }}
            />
          );
        };
        return <ConfirmacaoEmailWrapper />;
      })()}
      <ConfirmacaoEmailModal
        open={!!inscricaoPayload}
        onClose={() => setInscricaoPayload(null)}
        onConfirm={finalizarInscricao}
        previewPayload={inscricaoPayload ? {
          card_id: inscricaoPayload.card_id,
          quantidade: inscricaoPayload.quantidade,
          valor_vendido: inscricaoPayload.valor_vendido,
          valor_cheio: inscricaoPayload.valor_cheio,
          alunos_pessoa_ids: inscricaoPayload.alunos_pessoa_ids || [],
        } : null}
      />
    </div>
  );
}
